import {
  PropsWithChildren,
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
} from 'react';

import { ApolloProvider } from '@apollo/client';
import _ from 'lodash';

import { configApiRef, useAnalytics, useApi } from '@backstage/core-plugin-api';

import { createApolloClient } from '@spotify/confidence-graphql';

import { DialogProvider, useFeatureFlag } from '../../components';
import { Region } from '../../utils';
import { AuthFactoryProvider } from '../auth-factory';
import { CLIENT_ID } from '../auth-factory/auth0/auth';

export type ConfidenceCoreContextType = {
  config: { [x: string]: any };
  setConfig: (v: { [x: string]: any }) => void;
  displayNames: Map<string, string>;
  setDisplayName: (v: Record<string, string>) => void;
  region?: Region;
  isEmbedded: boolean;
  featureFlags: Record<string, boolean>;
};

const defaultValue: ConfidenceCoreContextType = {
  config: {
    clientId: CLIENT_ID,
  },
  setConfig: () => {},
  displayNames: new Map(),
  setDisplayName: () => {},
  isEmbedded: false,
  featureFlags: {},
};

const ConfidenceCoreContext =
  createContext<ConfidenceCoreContextType>(defaultValue);

export const ConfidenceCoreProvider = ({
  config: { clientId, redirectUri, ...c } = defaultValue.config,
  children,
  isEmbedded = false,
  featureFlags = {},
}: PropsWithChildren<
  Partial<
    Pick<ConfidenceCoreContextType, 'config' | 'isEmbedded' | 'featureFlags'>
  >
>) => {
  const [token, setToken] = useState<string>();
  const [region, setRegion] = useState<Region>();
  const [config, setConfig] = useState(c);
  const analyticsApi = useAnalytics();

  const { enabled: requestAccounting } = useFeatureFlag(
    'graphql-request-timing',
    { enabled: false },
  );

  const appConfig = useApi(configApiRef);

  const [displayNames, setDisplayNames] = useState<
    ConfidenceCoreContextType['displayNames']
  >(defaultValue.displayNames);

  const handleTokenReceived = (userToken: string, userRegion: Region) => {
    setToken(userToken);
    setRegion(userRegion);
  };

  const apolloClient = useMemo(() => {
    const uri = appConfig.getOptionalString('confidence.graphql')
      ? appConfig.getOptionalString('confidence.graphql')
      : `https://graphql-konfidens.spotify.com/graphql`;
    return createApolloClient({
      token,
      uri,
      region,
      analyticsApi: requestAccounting ? analyticsApi : undefined,
    });
  }, [token, region, analyticsApi]);

  const updateDisplayName = useCallback(
    (v: Record<string, string>) => {
      setDisplayNames(current => {
        const newMap = new Map(current);
        Object.entries(v).forEach(([key, value]) => {
          newMap.set(key, value);
        });
        if (_.isEqual(current, newMap)) {
          return current;
        }
        return newMap;
      });
    },
    [setDisplayNames],
  );

  const value = useMemo<ConfidenceCoreContextType>(
    () => ({
      config,
      setConfig,
      displayNames,
      setDisplayName: updateDisplayName,
      region,
      isEmbedded,
      featureFlags,
    }),
    [config, displayNames, setDisplayNames, isEmbedded, region, featureFlags],
  );

  return (
    <ConfidenceCoreContext.Provider value={value}>
      <AuthFactoryProvider
        clientId={clientId}
        redirectUri={redirectUri}
        onTokenReceived={handleTokenReceived}
      >
        <ApolloProvider client={apolloClient as any}>
          <DialogProvider>{children}</DialogProvider>
        </ApolloProvider>
      </AuthFactoryProvider>
    </ConfidenceCoreContext.Provider>
  );
};

export const useConfidenceCoreContext = () => useContext(ConfidenceCoreContext);

export const useDisplayNames = () => {
  const { displayNames, setDisplayName } = useConfidenceCoreContext();

  return {
    displayNames,
    setDisplayName,
  };
};

export const useRegion = () => {
  const { region } = useConfidenceCoreContext();
  return region;
};

export const useIsEmbedded = () => {
  const { isEmbedded } = useConfidenceCoreContext();
  return { isEmbedded };
};

export const useAppFeatureFlags = <
  T extends Record<string, boolean> = Record<string, boolean>,
>(): T => {
  const { featureFlags } = useConfidenceCoreContext();
  return featureFlags as T;
};
